import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CurrencyPipe, DatePipe } from "@angular/common";
import { Observable } from "rxjs";

import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { Table } from "primeng/table";

import { DataTableService } from "@data/services/utils/data-table.service";
import { SelectEnumPipe } from "@data/pipes/enum/select-enum.pipe";
import { LoaderService } from "@data/services/page/loader.service";
import { ITableCol } from "@data/interfaces/itable.interface";
import { AuthService } from "@data/services/modules/auth/auth.service";
import { defaultFilterMatchMode } from "./filter-match-mode.util";

@Component({
  selector: 'app-main-table',
  templateUrl: './main-table.component.html',
  styleUrls: ['./main-table.component.scss'],
  providers: [MessageService, ConfirmationService, SelectEnumPipe, CurrencyPipe, DatePipe]
})
export class MainTableComponent implements OnInit {
  @ViewChild('dtMainTable') dtMainTable!: Table;
  @Input() totalRecords = 0
  @Input() itemsByPage = 10
  @Input() additionalFilters = {}
  @Input() columns: ITableCol[] = []
  @Input() apiService!: { getAll(page: number, limit: number, filters: any): Observable<any> }
  @Input() actions: { icon: string, color: string, action: string, title: string, rule?: { field: string; value: any, type: string }, permission?: [string, string] | string | undefined }[] = []
  @Input() singleEntityName = ''
  @Input() pluralEntityName = ''
  @Input() additionalMainActions: { label: string, icon: string, action: string, permission?: [string, string] }[] = []
  @Output() onAction = new EventEmitter()
  @Output() onMainAction = new EventEmitter()
  menu!: MenuItem[];
  entities!: any[]
  filters = {}
  loadingTable = true
  //* Filas del esqueleto: diez, el tamaño de página por omisión, para que la tabla no cambie de alto
  //* al llegar los datos.
  readonly skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  readonly defaultMatchMode = defaultFilterMatchMode
  selectedEntities!: any[] | null;
  _selectedColumns!: ITableCol[]

  constructor(
    public dataTableService: DataTableService,
    private selectEnumPipe: SelectEnumPipe,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private auth: AuthService
  ) {

  }

  ngOnInit() {
    this.selectedColumns = this.columns.filter((c) => !c.hidden)
    this.constructItems()
  }

  constructItems() {
    this.menu = [
      {
        label: 'Descargar',
        icon: 'fa-solid fa-cloud-arrow-down',
        items: [
          {
            label: 'Excel',
            command: () => {
              this.exportExcel()
            }
          },
          {
            label: 'PDF',
            command: () => {
              this.exportPdf()
            }
          },
        ]
      },
      {
        label: 'Refrescar',
        icon: 'fa-solid fa-rotate-right',
        command: () => {
          this.findRecords()
        }
      },
      {
        label: 'Limpiar',
        icon: 'fa-solid fa-broom',
        command: () => {
          this.dtMainTable.clear()
        }
      },
    ]
    for (const additionalMainAction of this.additionalMainActions) {
      //* Si la acción declara `permission`, se oculta del menú para roles que no la
      //* tengan (antes se mostraba a todos → clic → 403 del backend).
      if (additionalMainAction.permission && !this.auth.userHasAuthorization(additionalMainAction.permission[0], additionalMainAction.permission[1])) continue
      this.menu.push({
        label: additionalMainAction.label,
        icon: additionalMainAction.icon,
        command: () => {
          this.onMainAction.emit(additionalMainAction.action)
        }
      })
    }
  }

  findRecords(
    page = 0,
    limit = this.itemsByPage,
    filters = {}
  ) {
    this.loadingTable = true
    
    this.apiService.getAll(page + 1, limit, { ...filters, ...this.additionalFilters }).subscribe(r => {
      this.totalRecords = r.data.totalItems !== undefined ? r.data.totalItems : (r.data.data?.length || 0)
      this.entities = r.data.data
      this.loadingTable = false
      
      // Forzar detección de cambios para componentes con OnPush
      this.cdr.detectChanges()
    })
  }

  exportPdf() {
    this.loaderService.showSpinner();
    this.apiService.getAll(0, 100000000, { ...this.dtMainTable.filters, ...this.additionalFilters }).subscribe(r => {
      const dataConverted = this.convertData(r.data.data);

      //* Procesamos los datos para que se muestren correctamente en el PDF
      const processedData = dataConverted.map((i: any) => {
        const item: any = {};
        for (const column of this.selectedColumns) item[column.field] = this.getValueByPath(i, column.field);

        return item;
      });

      const columns = [
        ...this.selectedColumns.map(sc => {
          return {
            dataKey: sc.field,
            title: sc.header
          }
        })
      ];

      this.dataTableService.exportPdf(columns, processedData)
        .then(pdfResponse => {
          this.loaderService.hideSpinner();
          this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'El archivo se ha descargado exitosamente', life: 5000 });
        })
        .catch(e => {
          this.loaderService.hideSpinner();
          this.messageService.add({ severity: 'danger', summary: 'Error', detail: 'Error al descargar el archivo', life: 5000 });
        });
    });
  }


  getValueByPath(obj: any, path: string): any {
    if (!path) return undefined;
    const pathArray = path.replace(/\[(\w+)\]/g, '.$1').split('.');
    let current = obj;

    for (const key of pathArray) {
      if (current == null) return undefined;
      current = current[key];
    }

    if (Array.isArray(current)) return current.map((i: any) => i).join(', ');

    return current;
  }

  exportExcel() {
    this.loaderService.showSpinner();
    this.apiService.getAll(0, 100000000, { ...this.dtMainTable.filters, ...this.additionalFilters }).subscribe(r => {
      const dataConverted = this.convertData(r.data.data);
      this.dataTableService.exportExcel(dataConverted.map((i: any) => {
        const response: any = {};
        for (const column of this.selectedColumns) {
          const ii = i as any;
          response[column.header] = this.getValueByPath(ii, column.field);
        }
        return response;
      }));
      this.loaderService.hideSpinner();
      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'El archivo se ha descargado exitosamente', life: 5000 });
    });
  }

  /**
   * Da formato a la fila para las dos exportaciones, y **resuelve las columnas derivadas**.
   *
   * El orden importa: la derivada se calcula sobre la fila CRUDA y antes de dar formato. Al revés
   * estaría restando cadenas ya formateadas —`'$50,000.00' - '$50,000.00'`— y el Excel que
   * Contabilidad cuadra saldría con `NaN` sin que nada avisara. Antes ni siquiera llegaba a eso:
   * como la derivada no existe en el documento, salía vacía.
   */
  convertData(data: any) {
    return data.map((i: any) => {
      const response = { ...i }

      for (const item of this.columns) {
        const raw = item.compute ? item.compute(i) : i[item.field]

        if (item.dataType === 'date') {
          response[item.field] = this.datePipe.transform(raw, 'dd-MM-YYYY', '-0600')
        } else if (item.dataType === 'enum') {
          response[item.field] = this.selectEnumPipe.transform(raw, item.enum)
        } else if (item.dataType === 'currency') {
          response[item.field] = this.currencyPipe.transform(raw)
        } else if (item.compute) {
          response[item.field] = raw
        }
      }
      return response
    })
  }

  /**
   * Ancho de la barra del esqueleto según lo que va a llegar a esa columna.
   *
   * Todas del mismo ancho se leen como un código de barras; con el ancho del tipo de dato, la tabla
   * en gris ya se parece a la que viene.
   */
  skeletonWidth(col: ITableCol): string {
    switch ((col as any)?.dataType) {
      case 'currency': return '5.5rem'
      case 'number':
      case 'numeric': return '3.5rem'
      case 'date': return '5.5rem'
      case 'timeElapsed': return '6rem'
      case 'boolean': return '1.2rem'
      case 'enum':
      case 'channel': return '6.5rem'
      case 'progress': return '8rem'
      default: return '9rem'
    }
  }

  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    this._selectedColumns = this.columns.filter((col) => val.includes(col));
  }

  clear(table: Table) {
    table.clear();
  }

  getValue($event: Event) {
    return ($event.target as HTMLInputElement).value
  }

  loadEntities(e: any) {
    const currentPage = e.first / this.itemsByPage
    this.itemsByPage = e.rows
    this.findRecords(currentPage, this.itemsByPage, e.filters)
  }

  getValueObject(obj: any, keys: string) {
    const keysArray = keys.split('.');
    let value: any = obj;

    for (const key of keysArray) {
      if (value === undefined || value === null || value === '') {
        return 'N/A';
      }
      value = value[key];
    }

    return value !== undefined && value !== null && value !== '' ? value : 'N/A';
  }

  // Fallback declarativo por columna: primer path con valor real ('N/A'/''/null no cuentan).
  // Sin col.fallbackFields se comporta idéntico a getValueObject. Ej: columna Activos con
  // fallback al provitionalAsset de la QP cuando el lease se originó con activo provisional.
  getCellValue(item: any, col: ITableCol): any {
    //* La columna derivada manda: su `field` es solo su llave, no un path que exista en la fila.
    if (col.compute) return col.compute(item)
    const primary = this.getValueObject(item, col.field)
    if (this.hasValue(primary)) return primary
    for (const field of col.fallbackFields ?? []) {
      const value = this.getValueObject(item, field)
      if (this.hasValue(value)) return value
    }
    return primary
  }

  roundNumber(n: number) {
    return Math.round(n)
  }

  // Evita pasar 'N/A'/null/'' a los pipes de valor (currency/date/timeElapsed),
  // que lanzan NG02100. getValueObject devuelve 'N/A' para campos vacíos.
  hasValue(v: any): boolean {
    return v !== null && v !== undefined && v !== '' && v !== 'N/A'
  }

  showAction(data: any, rule: { field: string; value: any, type: string } | undefined) {
    if (rule === undefined) return true
    else if (rule.type === 'equals' && data[rule.field] === rule.value) return true
    else if (rule.type === 'notEquals' && data[rule.field] !== rule.value) return true
    else if (rule.type === 'gte' && data[rule.field] >= rule.value) return true
    return false
  }
}
